import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useSelector } from 'react-redux';
import { AnimatePresence, motion } from 'framer-motion';
import {
	ModalHeader,
	ModalBody,
	ModalFooter,
	Button,
	Spinner,
	Input,
	Select,
	SelectItem,
	Autocomplete,
	AutocompleteItem,
	Tooltip,
	addToast
} from '@heroui/react';
import { convertKgToLbs, convertLbsToKg } from '../../utils/weight-conversion.js';
import {
	RefreshCcw,
	Plus,
	Copy,
	Pencil,
	GripVertical,
	UserPlus,
	CheckCircle2,
	XCircle,
	AlertTriangle
} from 'lucide-react';
import CalendarApi from '../../../api/CalendarApi.js';
import { describeWbError } from '../utils/describeWbError.js';
import ResourcesApi from '../../../api/resourcesApi.js';
import {
	pickCalendarBootstrap,
	pickCalendarFilters,
	pickCalendarRefs
} from '../../calendar-common/viewStoreKeys.js';
import { canManageEventWeightBalance, isWbEventOwner } from '../utils/eventPermissions.js';
import {
	unwrapWeightBalanceResponse,
	toWeightBalanceDraft,
	taxiBurnForApi,
	buildWeightBalanceRows,
	buildWbPoints,
	buildFuelCasePoints,
	cleanNumDisplay
} from '../utils/weightBalanceAdapters.js';
import { buildWeightBalanceManifest } from '../utils/buildWeightBalanceManifest.js';
import { PrinterService } from '../../printer/printer.service.js';
import EnvelopeChart from '../../common/EnvelopeChart.js';

// Highlighted rows match Pencil qYZ2S frame — Ramp/Takeoff/Landing each get a
// green tint + bold value + left accent stripe.
const STRONG_PARAMETERS = ['Ramp Weight & CG', 'Takeoff Weight & CG', 'Landing Weight & CG'];

const WEIGHT_UNITS = {
	lbs: 'lbs',
	kg: 'kg',
	percent: '%',
	gal: 'gal',
	liters: 'liters'
};

const FIELD_NAME_MAP = {
	'Fuel Weight': 'fuelWeight',
	'Taxi & Run-up Fuel Burn': 'taxiRunUpFuelBurn',
	'Duration of Flight': 'eventDuration',
	'Fuel Load': 'fuelLoad'
};

// `userPk` / `type` carry the linked event-participant identity when the form
// was filled via "Choose from event participants" (W2). Manual entries keep
// userPk 0 / type null. Persisted into the saved passenger so the participant
// picker can dedupe already-added crew by userPk.
const PAX_FORM_DEFAULTS = {
	fullName: '',
	weight: '',
	unit: 'lbs',
	phoneNumber: '',
	email: '',
	userPk: 0,
	type: null
};

// Role labels for the "Choose from event participants" picker — mirror legacy
// `passenger.modal.html` wording. Keys = Symfony `PassengerType` enum values.
const PARTICIPANT_ROLE_LABELS = {
	pilot: 'Pilot',
	second_pilot: 'SIC Pilot',
	instructor: 'Instructor'
};

function formatNum(val) {
	if (val === null || val === undefined || val === '') return '-';
	const n = parseFloat(String(val).replace(',', '.'));
	if (isNaN(n)) return String(val);
	return n.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

function buildWbPayload(fields, unit, passengers) {
	const rows = fields
		.filter(f => f.group === 'row')
		.map(f => ({ name: f.weight.name, weight: parseFloat(f.weight.value || 0).toFixed(2) }));
	const scalars = {};
	fields.forEach(f => {
		if (f.weight?.editable && !f.group) {
			const key = FIELD_NAME_MAP[f.id];
			if (key) {
				const digits = f.id === 'Duration of Flight' ? 10 : 2;
				// Taxi burn goes over the wire as a positive magnitude — Symfony 422s
				// on a negative. The subtraction itself is server-side; see
				// `taxiBurnForApi` for the history.
				scalars[key] =
					key === 'taxiRunUpFuelBurn'
						? taxiBurnForApi(f.weight.value)
						: parseFloat(f.weight.value || 0).toFixed(digits);
			}
		}
	});
	return { unit, rows, passengers: passengers || [], ...scalars };
}

function buildPassengerMatrix(passengers, passengerRows) {
	return passengerRows.map((row, r) => row.seats.map((_, c) => passengers?.[r]?.[c] ?? null));
}

/**
 * WeightBalanceScreen — Month-native W&B screen (sub-epics B + C + D).
 *
 * Props:
 *   - event       {Object}          Full event object
 *   - eventId     {number|string}
 *   - orgUnique   {string}          Effective org slug (event's own org)
 *   - aircraftId  {number|string|null}
 *   - onBack      {Function}        Navigate back to event view
 *   - onSaved     {Function}        Called after the explicit Save button —
 *                                   parent navigates back to view + refetches.
 *   - onPersisted {Function}        Called after a passenger CRUD auto-save —
 *                                   parent refreshes the month grid WITHOUT
 *                                   navigating away or remounting this screen
 *                                   (Rem38 M6). Distinct from `onSaved` because
 *                                   the user must stay in the W&B editor while
 *                                   adding/removing/moving passengers.
 */
/**
 * Seat chips are ~84px wide, so a full "Name Surname" never fits — but the
 * bare truncation cut into the FIRST name ("Ole…", USER screenshot). Show the
 * first name whole and shorten only the surname to an initial: "Oleh V." —
 * the full name stays in the title tooltip.
 */
function seatDisplayName(fullName) {
	const parts = String(fullName || '')
		.trim()
		.split(/\s+/);
	if (parts.length < 2) return fullName || '';
	const surnames = parts.slice(1).map(w => `${w[0]}.`);
	return `${parts[0]} ${surnames.join(' ')}`;
}

function WeightBalanceScreen({
	event,
	eventId,
	orgUnique,
	aircraftId,
	onBack,
	onSaved,
	onPersisted
}) {
	const [loading, setLoading] = useState(true);
	// Structured failure descriptor from `describeWbError`, not a bare string —
	// the error state needs a cause and a way out, not just a sentence.
	const [error, setError] = useState(null);
	// Bumped by Retry to re-run the load effect.
	const [reloadToken, setReloadToken] = useState(0);
	const [draft, setDraft] = useState(null);
	// Raw W&B server payload — kept for buildWeightBalanceManifest
	const [wbPayload, setWbPayload] = useState(null);
	const [envelope, setEnvelope] = useState(null);
	const [fields, setFields] = useState([]);
	const [unit, setUnit] = useState('%');
	const [wbPoints, setWbPoints] = useState([]);
	const [fuelCasePoints, setFuelCasePoints] = useState(null);
	const [recalculating, setRecalculating] = useState(false);
	const [saving, setSaving] = useState(false);
	const [printing, setPrinting] = useState(false);

	// Passenger editor
	const [editingPax, setEditingPax] = useState(null); // { rowIndex, seatIndex } | null
	const [paxForm, setPaxForm] = useState(PAX_FORM_DEFAULTS);
	const [dragSource, setDragSource] = useState(null); // { rowIndex, seatIndex } | null

	const reduxUser = useSelector(state => state?.user);
	// HIGH (P-Root2): on Week/Day the hardcoded `calendarMonth*` keys were
	// undefined → the manifest silently fell back to UTC. Route through the
	// view-aware pickers so W&B start/finish print in the active view's tz.
	const reduxTimezone = useSelector(
		state =>
			state?.timezone ||
			pickCalendarFilters(state)?.timezone ||
			pickCalendarBootstrap(state)?.timezone ||
			'UTC'
	);
	// The legacy `state.activityTypes` branch is dead (no such root slice on any
	// calendar view); route through the view-aware refs picker.
	const reduxActivityTypes = useSelector(state => pickCalendarRefs(state)?.activityTypes || null);
	const reduxResources = useSelector(state => state?.resources);
	const reduxOrganization = useSelector(state => state?.organization);
	const currentUserId = useSelector(
		state => state?.user?.id ?? state?.user?.userId ?? state?.user?._userID ?? null
	);
	// Cross-org accessGroups map: current org's bootstrap flags + the Phase-3
	// fan-out for other orgs — mirrors `EventViewModal`'s `accessGroupsMap`,
	// which resolves the EVENT's own org rather than the page org.
	const accessGroupsMap = useSelector(state => {
		const bs = pickCalendarBootstrap(state);
		const currentOrgUnique = bs.organization?.unique || bs.orgUnique || null;
		const map = { ...(bs.eventOrgPerms || {}) };
		if (currentOrgUnique && bs.accessGroups) {
			map[currentOrgUnique] = bs.accessGroups;
		}
		return map;
	});
	const canManageWeightBalance = useMemo(
		() => canManageEventWeightBalance(event, accessGroupsMap),
		[event, accessGroupsMap]
	);
	const isWbOwner = useMemo(
		() => isWbEventOwner(event, currentUserId, reduxResources),
		[event, currentUserId, reduxResources]
	);
	// Ownership decides only whether the write controls are enabled — never
	// which route is called. The backend re-checks ownership on the
	// my-route, so a wrong client-side guess can never write the wrong event.
	const canWriteWb = canManageWeightBalance || isWbOwner;
	const persistWeightAndBalance = useCallback(
		payload =>
			canManageWeightBalance
				? CalendarApi.updateWeightAndBalance(eventId, payload, orgUnique)
				: CalendarApi.updateMyWeightAndBalance(eventId, payload, orgUnique),
		[canManageWeightBalance, eventId, orgUnique]
	);

	// Passenger rows from the W&B `rows` array (legacy parity — `PassengerSeats`
	// in `passegers-seats.js:32` consumes `seats={rows}` with the same shape).
	// Each entry: `{ name, seats: <integer count>, arm, weight }`. We expand
	// `seats` into an array of `null`s here so the existing render can keep
	// using `row.seats.map(...)`.
	const passengerRows = useMemo(() => {
		if (!Array.isArray(draft?.rows)) return [];
		return draft.rows.map(row => ({
			name: row.name,
			seats: Array(Number(row.seats) || 0).fill(null),
			arm: row.arm,
			weight: row.weight
		}));
	}, [draft?.rows]);

	const applyWbPayload = useCallback(payload => {
		setWbPayload(payload);
		setDraft(toWeightBalanceDraft(payload));
		const newFields = buildWeightBalanceRows(payload.weightAndBalanceTable, payload.warning || []);
		setFields(newFields);
		setUnit(payload.unit || '%');
		setWbPoints(buildWbPoints(payload.weightAndBalanceTable));
		setFuelCasePoints(buildFuelCasePoints(payload.weightAndBalanceTable, payload.fuelWeightTable));
	}, []);

	// Persist an explicit passenger matrix (used by the passenger CRUD handlers
	// with the live matrix to avoid stale-closure issues with draft.passengers).
	//
	// Rem38 M6: passenger add / remove / drag-move must PERSIST. The previous
	// `recalculateWithPassengers` hit the WHAT-IF `recalculate` endpoint, so a
	// passenger change updated only the live preview and was LOST unless the user
	// then pressed Save. Legacy parity (`weight-and-balance.js`, user Network
	// capture 2026-05-29) saves on every passenger change.
	//
	// `updateWeightAndBalance` (POST .../update/{id}) returns the full
	// `ResponseWeightAndBalanceDto` — the SAME shape as the GET load (verified in
	// `CalendarWeightAndBalanceManager::saveWeightAndBalance` vs `::getWeightAndBalance`,
	// both return passengers + weightAndBalanceTable + fuelWeightTable + rows). So
	// we feed the save response straight into `applyWbPayload` and SKIP a separate
	// `getWeightAndBalance` re-fetch (one request, not three).
	//
	// We call `onPersisted?.()` (NOT `onSaved?.()`): the parent's `onSaved`
	// navigates back to the event view AND bumps `refetchKey`, which would
	// unmount this screen and trigger a slow event re-fetch on EVERY passenger
	// add/remove/move. `onPersisted` only refreshes the month grid (via the
	// parent's `onUpdated`), so the user stays in the editor. The persisted
	// passenger state is read fresh by Check Out/In from the backend bootstrap.
	// The manual Recalculate button (`handleRecalculate`) keeps the `recalculate`
	// endpoint — it stays a preview-only scenario tool.
	const savePassengerChange = useCallback(
		async newPassengers => {
			if (!eventId || !orgUnique) return;
			setRecalculating(true);
			try {
				const payload = buildWbPayload(fields, unit, newPassengers);
				const res = await persistWeightAndBalance(payload);
				const data = unwrapWeightBalanceResponse(res);
				if (data && data.weightAndBalanceTable) {
					// Full DTO from the save — reuse the GET-load path verbatim.
					applyWbPayload(data);
				} else {
					// Defensive: empty DTO (e.g. no aviation resource) — keep the
					// local matrix so the grid still reflects the user's change.
					applyWbPayload({ ...draft, passengers: newPassengers });
				}
				onPersisted?.();
			} catch (e) {
				addToast({
					title: 'Save failed',
					description: describeWbError(e).hint || e.message,
					color: 'danger'
				});
			} finally {
				setRecalculating(false);
			}
		},
		[eventId, orgUnique, fields, unit, draft, applyWbPayload, onPersisted, persistWeightAndBalance]
	);

	useEffect(() => {
		if (!eventId || !orgUnique) return;
		setLoading(true);
		setError(null);

		Promise.allSettled([
			CalendarApi.getWeightAndBalance(eventId, orgUnique),
			aircraftId ? ResourcesApi.getEnvelope(aircraftId, orgUnique) : Promise.resolve(null)
		]).then(([wbResult, envResult]) => {
			if (wbResult.status === 'fulfilled') {
				try {
					const payload = unwrapWeightBalanceResponse(wbResult.value);
					applyWbPayload(payload);
				} catch (e) {
					setError(describeWbError(e));
				}
			} else {
				setError(describeWbError(wbResult.reason));
			}

			if (envResult.status === 'fulfilled' && envResult.value != null) {
				const raw = envResult.value;
				setEnvelope(raw?.data?.data || raw?.data || raw || null);
			}

			setLoading(false);
		});
	}, [eventId, orgUnique, aircraftId, applyWbPayload, reloadToken]);

	const handleChange = useCallback((index, value) => {
		setFields(prev => {
			const next = [...prev];
			next[index] = { ...next[index], weight: { ...next[index].weight, value } };
			return next;
		});
	}, []);

	const handleChangeFuelLoadType = useCallback(newUnit => {
		setUnit(newUnit);
	}, []);

	// `fieldsArg` (optional) recalculates from an explicit fields array instead
	// of state — used by the min-fuel auto-fill, whose new value hasn't
	// committed to state yet when the recalc fires. The Recalculate button
	// passes a press event here; the Array guard routes that to state fields.
	const handleRecalculate = useCallback(
		async fieldsArg => {
			if (!eventId || !orgUnique || recalculating) return;
			const fieldsList = Array.isArray(fieldsArg) ? fieldsArg : fields;
			setRecalculating(true);
			try {
				const payload = buildWbPayload(fieldsList, unit, draft?.passengers || []);
				const res = await CalendarApi.recalculateWeightAndBalance(eventId, payload, orgUnique);
				const data = unwrapWeightBalanceResponse(res);
				if (data) {
					const updatedPayload = {
						...draft,
						weightAndBalanceTable: data.weightAndBalanceTable || draft?.weightAndBalanceTable,
						fuelWeightTable: data.fuelWeightTable || draft?.fuelWeightTable,
						unit: data.unit || unit
					};
					applyWbPayload(updatedPayload);
				}
			} catch (e) {
				addToast({
					title: 'Recalculate failed',
					description: describeWbError(e).hint || e.message,
					color: 'danger'
				});
			} finally {
				setRecalculating(false);
			}
		},
		[eventId, orgUnique, recalculating, fields, unit, draft, applyWbPayload]
	);

	// Fuel-case value in the CURRENT display unit (shared by the copy shortcut
	// and the min-fuel auto-fill).
	const fuelValueFor = useCallback(
		fuelKey => {
			const fuelData = draft?.fuelWeightTable?.[fuelKey];
			if (!fuelData) return null;
			const unitKey = unit === '%' ? 'percent' : unit === 'liters' ? 'liter' : unit;
			return fuelData[unitKey] || fuelData.lbs || '0';
		},
		[draft?.fuelWeightTable, unit]
	);

	const handleFuelCopy = useCallback(
		fuelKey => {
			const value = fuelValueFor(fuelKey);
			if (value == null) return;
			const fuelLoadIndex = fields.findIndex(f => f.id === 'Fuel Load');
			if (fuelLoadIndex !== -1) handleChange(fuelLoadIndex, cleanNumDisplay(value));
		},
		[fuelValueFor, fields, handleChange]
	);

	// Owner ask (2026-07-10): opening the screen with Fuel Load = 0 (or empty)
	// auto-applies the Min Fuel shortcut AND recalculates once, so the CG
	// tables reflect the filled fuel — a 0-fuel flight is never real, so
	// typing the minimum + pressing Recalculate was pure admin. NOT saved:
	// the user can still overtype ANY value (including 0) before saving.
	// The recalc gets the PATCHED fields explicitly — the new value hasn't
	// committed to React state yet. ONE-SHOT per open — the ref arms on the
	// first post-load pass whether or not the fill applies, so later
	// recalculates/saves never re-trigger it.
	const autoMinFuelRef = useRef(false);
	useEffect(() => {
		if (loading || autoMinFuelRef.current) return;
		if (!draft || fields.length === 0) return; // load not applied yet
		autoMinFuelRef.current = true;
		const fuelLoadIndex = fields.findIndex(f => f.id === 'Fuel Load');
		const rawValue = fuelValueFor('min');
		if (fuelLoadIndex === -1 || rawValue == null) return;
		const current = parseFloat(String(fields[fuelLoadIndex].weight?.value ?? '').replace(',', '.'));
		if (Number.isFinite(current) && current !== 0) return; // only 0/empty
		const value = cleanNumDisplay(rawValue);
		handleChange(fuelLoadIndex, value);
		const patched = fields.map((f, i) =>
			i === fuelLoadIndex ? { ...f, weight: { ...f.weight, value } } : f
		);
		handleRecalculate(patched);
	}, [loading, fields, draft, fuelValueFor, handleChange, handleRecalculate]);

	const handleSave = useCallback(async () => {
		if (!eventId || !orgUnique || saving) return;
		setSaving(true);
		try {
			const payload = buildWbPayload(fields, unit, draft?.passengers || []);
			await persistWeightAndBalance(payload);
			addToast({ title: 'W&B saved successfully', color: 'success' });
			onSaved?.();
			onBack?.();
		} catch (e) {
			addToast({
				title: 'Save failed',
				description: describeWbError(e).hint || e.message,
				color: 'danger'
			});
		} finally {
			setSaving(false);
		}
	}, [eventId, orgUnique, saving, fields, unit, draft, onSaved, onBack, persistWeightAndBalance]);

	// Imperative handle to the EnvelopeChart so the print manifest can capture a
	// live screenshot of the on-screen envelope (see `handlePrintManifest`).
	const envelopeRef = useRef(null);

	const handlePrintManifest = useCallback(async () => {
		if (!wbPayload || printing) return;
		setPrinting(true);
		try {
			// Capture a live screenshot of the on-screen envelope and pass it as a
			// SEPARATE `envelopeImage` field — do NOT overwrite the static `.image`,
			// which one client uploads with extra data the manifest must preserve.
			// The manifest renders both when a static image exists, else just the
			// live chart. Build a fresh payload — do NOT mutate `wbPayload` state.
			const envelopeImage = envelopeRef.current?.toImage();
			await PrinterService.print(() =>
				buildWeightBalanceManifest({
					event,
					weightAndBalance: { ...wbPayload, envelopeImage },
					user: reduxUser,
					timezone: reduxTimezone,
					activityTypes: reduxActivityTypes,
					resources: reduxResources,
					organization: reduxOrganization
				})
			);
		} catch (e) {
			addToast({
				title: 'Print Manifest failed',
				description: e?.message || 'Could not render manifest',
				color: 'danger'
			});
		} finally {
			setPrinting(false);
		}
	}, [
		event,
		wbPayload,
		printing,
		reduxUser,
		reduxTimezone,
		reduxActivityTypes,
		reduxResources,
		reduxOrganization
	]);

	// ── Passenger CRUD handlers ──────────────────────────────────────────────────

	const handlePaxOpen = useCallback(
		(rowIndex, seatIndex) => {
			if (!canWriteWb) return;
			const pax = draft?.passengers?.[rowIndex]?.[seatIndex];
			// Passenger unit defaults to `lbs` (legacy parity — `passenger.consts.js`
			// `DEFAULT_PASSENGER.unit = lbs`). Do NOT inherit from the table-level
			// `unit` state, which represents fuel-load units (`%` / `lbs` / `kg` /
			// `gal` / `liters`) and isn't a passenger weight unit.
			setPaxForm(
				pax
					? {
							fullName: pax.fullName || '',
							weight: String(pax.weight || ''),
							unit: pax.unit || 'lbs',
							phoneNumber: pax.phoneNumber || '',
							email: pax.email || '',
							userPk: pax.userPk ?? 0,
							type: pax.type ?? null
						}
					: { ...PAX_FORM_DEFAULTS }
			);
			setEditingPax({ rowIndex, seatIndex });
		},
		[draft, unit, canWriteWb]
	);

	const handlePaxClose = useCallback(() => {
		setEditingPax(null);
		setPaxForm(PAX_FORM_DEFAULTS);
	}, []);

	// W2 — "Choose from event participants" picker. Source = the W&B payload's
	// `availablePassengers` (Symfony `Event::getAvailablePassengers()` →
	// `{ fullName, weight, type, userPk, phoneNumber, email }` for confirmed
	// Pilot / SIC Pilot / Instructor). Exclude participants already seated
	// (by userPk) so a crew member can't be double-added. Legacy parity:
	// `passenger.modal.html` "Choose from event participants:" Apply list.
	const availableParticipants = useMemo(() => {
		const list = Array.isArray(draft?.availablePassengers) ? draft.availablePassengers : [];
		if (!list.length) return [];
		const seatedUserPks = new Set();
		(Array.isArray(draft?.passengers) ? draft.passengers : []).forEach(row => {
			(Array.isArray(row) ? row : []).forEach(seat => {
				if (seat && seat.userPk) seatedUserPks.add(String(seat.userPk));
			});
		});
		return list.filter(p => p && p.userPk != null && !seatedUserPks.has(String(p.userPk)));
	}, [draft?.availablePassengers, draft?.passengers]);

	const handleApplyParticipant = useCallback(participant => {
		// Populate the form; do NOT auto-submit (user can still adjust + Save).
		// `unit` stays the passenger default 'lbs' (weight unit, not fuel unit).
		setPaxForm({
			fullName: participant.fullName || '',
			weight: participant.weight != null ? String(participant.weight) : '',
			unit: 'lbs',
			phoneNumber: participant.phoneNumber || '',
			email: participant.email || '',
			userPk: participant.userPk ?? 0,
			type: participant.type ?? null
		});
	}, []);

	const handlePaxSave = useCallback(async () => {
		if (!canWriteWb) return;
		// Rem38 M5 — block empty / zero / negative weight (mirrors the Save-button
		// disabled gate below).
		if (!editingPax || !paxForm.fullName.trim() || !paxForm.weight || Number(paxForm.weight) <= 0)
			return;
		const { rowIndex, seatIndex } = editingPax;
		const existingPax = draft?.passengers?.[rowIndex]?.[seatIndex];
		const newPax = {
			...(existingPax || {}),
			fullName: paxForm.fullName.trim(),
			weight: paxForm.weight,
			unit: paxForm.unit,
			// W2 — preserve the linked participant identity (set by "Choose from
			// event participants"); fall back to the existing seat's, else manual.
			userPk: paxForm.userPk || existingPax?.userPk || 0,
			type: paxForm.type ?? existingPax?.type ?? null,
			bookingId: existingPax?.bookingId ?? '',
			bookingLongId: existingPax?.bookingLongId ?? '',
			bookingUrl: existingPax?.bookingUrl ?? '',
			phoneNumber: paxForm.phoneNumber || '',
			email: paxForm.email || ''
		};
		const matrix = buildPassengerMatrix(draft?.passengers, passengerRows);
		matrix[rowIndex][seatIndex] = newPax;
		setEditingPax(null);
		setPaxForm(PAX_FORM_DEFAULTS);
		await savePassengerChange(matrix);
	}, [editingPax, paxForm, draft, passengerRows, savePassengerChange, canWriteWb]);

	const handlePaxRemove = useCallback(
		async (rowIndex, seatIndex) => {
			if (!canWriteWb) return;
			const matrix = buildPassengerMatrix(draft?.passengers, passengerRows);
			matrix[rowIndex][seatIndex] = null;
			if (editingPax?.rowIndex === rowIndex && editingPax?.seatIndex === seatIndex) {
				setEditingPax(null);
				setPaxForm(PAX_FORM_DEFAULTS);
			}
			await savePassengerChange(matrix);
		},
		[draft, passengerRows, editingPax, savePassengerChange, canWriteWb]
	);

	const handleDragStart = useCallback(
		(rowIndex, seatIndex) => {
			if (!canWriteWb) return;
			setDragSource({ rowIndex, seatIndex });
		},
		[canWriteWb]
	);

	const handleDrop = useCallback(
		async (targetRow, targetCol) => {
			if (!canWriteWb) return;
			if (!dragSource) return;
			const { rowIndex: srcRow, seatIndex: srcCol } = dragSource;
			setDragSource(null);
			if (srcRow === targetRow && srcCol === targetCol) return;
			const matrix = buildPassengerMatrix(draft?.passengers, passengerRows);
			const tmp = matrix[srcRow][srcCol];
			matrix[srcRow][srcCol] = matrix[targetRow][targetCol];
			matrix[targetRow][targetCol] = tmp;
			await savePassengerChange(matrix);
		},
		[dragSource, draft, passengerRows, savePassengerChange, canWriteWb]
	);

	// Envelope limits
	const forwardLimits =
		envelope?.envelopCgLimit?.forward_cg_limit || envelope?.envelopCgLimit?.forwardCgLimit || [];
	const aftLimits =
		envelope?.envelopCgLimit?.aft_cg_limit || envelope?.envelopCgLimit?.aftCgLimit || [];
	const unitLabels = {
		weight: envelope?.unitWeightCG || 'lbs',
		cg: envelope?.unitForwardAftCG || 'in'
	};

	// Include per-row entries (Front Row / Aft Row / Side Bag / Aft Bag) in the
	// middle table — legacy parity. Adapter pushes them with `group: 'row'`.
	const tableFields = fields.filter(f => f.title);
	const statusMessages = fields
		.filter(f => !f.title && Array.isArray(f.status))
		.flatMap(f => f.status || []);

	const fuelTableKeys = draft?.fuelWeightTable ? Object.keys(draft.fuelWeightTable) : [];
	const copyKeys = ['min', 'max', 'reserve'];
	const hasEnvelopeData = forwardLimits.length > 0 || aftLimits.length > 0;

	// W&B is "within limits" when every highlight row (Ramp / Takeoff / Landing
	// Weight & CG) has an empty `status` array AND there are no global warnings.
	// Print Manifest is gated on this: signing off an out-of-limits manifest is a
	// safety problem, so the button is disabled with a tooltip explaining why.
	const wbWithinLimits = useMemo(() => {
		let anyChecked = false;
		const anyOut = fields.some(f => {
			if (!f.title || !STRONG_PARAMETERS.includes(f.title)) return false;
			// Adapter (`buildWeightBalanceRows`) writes `status` on the field
			// directly, NOT inside `field.weight`. Same bug as the row-highlight
			// fix earlier — if you read `f.weight?.status` here, the gate never
			// evaluates and Print Manifest stays permanently disabled.
			const s = f.status;
			if (!Array.isArray(s)) return false;
			anyChecked = true;
			return s.length > 0;
		});
		if (anyOut) return false;
		const anyWarning = fields.some(
			f => !f.title && Array.isArray(f.status) && f.status.some(it => !it?.success)
		);
		if (anyWarning) return false;
		// If no highlight rows have been evaluated yet (data not loaded), treat as
		// not-within-limits to be safe — Print Manifest stays disabled until a
		// Recalculate populates the status arrays.
		return anyChecked;
	}, [fields]);

	const isBusy = recalculating || saving;

	return (
		<>
			<ModalHeader className="flex items-center gap-3 border-b border-default-200 pr-10">
				<Button size="sm" variant="light" onPress={() => onBack?.()}>
					← Back
				</Button>
				<span
					className="text-lg font-bold flex-1"
					style={{ color: 'hsl(var(--heroui-foreground))' }}
				>
					Weight &amp; Balance
				</span>
			</ModalHeader>

			<ModalBody className="overflow-y-auto p-3">
				{loading && (
					<div className="flex justify-center items-center py-16">
						<Spinner size="lg" />
					</div>
				)}

				{!loading && error && (
					<div className="flex flex-col items-center text-center gap-2 py-14 px-6">
						<div className="flex items-center justify-center w-11 h-11 rounded-full bg-danger-50 mb-1">
							<AlertTriangle className="w-5 h-5 text-danger" aria-hidden="true" />
						</div>
						<p className="text-[15px] font-semibold text-foreground">{error.title}</p>
						{error.hint && <p className="text-sm text-default-500 max-w-[440px]">{error.hint}</p>}
						{error.status != null && (
							<p className="text-[11px] text-default-400">HTTP {error.status}</p>
						)}
						<div className="flex gap-2 mt-2">
							{error.canRetry && (
								<Button
									size="sm"
									color="primary"
									variant="flat"
									startContent={<RefreshCcw className="w-4 h-4" />}
									onPress={() => setReloadToken(n => n + 1)}
								>
									Try again
								</Button>
							)}
							<Button size="sm" variant="bordered" onPress={onBack}>
								Back to event
							</Button>
						</div>
					</div>
				)}

				{/* xl 3-col: the right panel (fuel table + CG Envelope) caps at the chart's
				    480px max, so the free width goes to the passengers column instead of
				    empty space beside the chart (owner). */}
				{!loading && !error && draft && (
					<div className="grid grid-cols-1 md:grid-cols-[minmax(0,1fr)_minmax(0,1.5fr)] xl:grid-cols-[minmax(300px,1fr)_max-content_minmax(320px,480px)] gap-2 px-2 min-h-0">
						{/* ── Left: Passenger sidebar ── */}
						{/* Inner column scroll + side divider + gutter are DESKTOP-3-col
						    only (epic REM-WBStacked). Below xl the section flows at natural
						    height under the single modal-body scroll — no chopped sub-scroll,
						    no stray vertical divider. */}
						<div className="space-y-4 xl:overflow-y-auto xl:pr-2 xl:border-r xl:border-default-100">
							{passengerRows.length === 0 && (
								<p className="text-default-400 text-sm italic">No passenger rows</p>
							)}
							{passengerRows.map((row, rowIndex) => (
								<div key={row.name}>
									<p className="text-sm font-semibold text-default-700 mb-2">{row.name}:</p>
									{row.seats.length === 0 && (
										<p className="text-xs italic text-default-400 pl-1 mb-1">Row without seats</p>
									)}
									{/* Responsive grid: as many ≥64px columns as fit, equal-width
									    cards, automatic wrap to multiple lines within this row's own
									    block. Replaces the old `flex flex-wrap` + `flex-1 basis-0`
									    approach, which split the row evenly and shrank cards past
									    ~3-4 seats instead of wrapping. (epic Task:11)
									    Min track is 64px (down from 84px, epic Task:17) so a typical
									    ≤4-seat row stays on ONE line in the narrowest (xl 300px ≈
									    282–290px usable) sidebar: 4×64 + 3×6 gap = 274 ≤ ~282. Many-
									    seat rows (8-10) still wrap ~4 per line; wider md/mobile layouts
									    auto-fill more. 64px → ~48px content (px-2), still legible. */}
									<div className="grid grid-cols-[repeat(auto-fill,minmax(84px,1fr))] gap-1.5">
										{row.seats.map((seat, seatIndex) => {
											const pax = draft.passengers?.[rowIndex]?.[seatIndex];
											const isEditing =
												editingPax?.rowIndex === rowIndex && editingPax?.seatIndex === seatIndex;
											const isDragOver =
												dragSource &&
												!(dragSource.rowIndex === rowIndex && dragSource.seatIndex === seatIndex);

											let seatStateClass;
											if (!canWriteWb) {
												seatStateClass =
													'cursor-default pointer-events-none' +
													(pax
														? ' border-primary-200 bg-primary/10'
														: ' border-dashed border-default-300 bg-content1 opacity-60');
											} else if (isEditing) {
												seatStateClass = 'border-primary-300 bg-primary/10 cursor-pointer';
											} else if (pax) {
												seatStateClass =
													'border-primary-200 bg-primary/10 hover:bg-primary/15 cursor-grab active:cursor-grabbing';
											} else {
												seatStateClass =
													'border-dashed border-default-300 bg-content1 hover:border-primary-300 hover:bg-default-50 cursor-pointer';
											}

											let seatTitle;
											if (pax) {
												seatTitle = canWriteWb
													? 'Click to edit · drag to move'
													: pax.fullName || 'Passenger';
											} else {
												seatTitle = canWriteWb ? 'Add passenger' : undefined;
											}

											let seatAriaLabel;
											if (pax) {
												seatAriaLabel = canWriteWb
													? `Edit passenger ${pax.fullName} (drag to move)`
													: `View passenger ${pax.fullName}`;
											} else {
												seatAriaLabel = `Add passenger to seat ${seatIndex + 1} in ${row.name}`;
											}

											return (
												<div key={seatIndex} className="min-w-0">
													{/* Seat card — Pencil qYZ2S design: filled cards use primary
													    tint + solid border; empty cards use dashed border + Plus
													    affordance. */}
													<div
														className={[
															'group rounded-lg border px-2 py-1.5 transition-colors relative min-h-[52px] flex items-center',
															seatStateClass,
															isDragOver ? 'border-dashed border-primary-400 bg-primary/5' : ''
														].join(' ')}
														role="button"
														title={seatTitle}
														aria-label={seatAriaLabel}
														draggable={!!pax && canWriteWb}
														onClick={() => handlePaxOpen(rowIndex, seatIndex)}
														onDragStart={e => {
															if (!pax) {
																e.preventDefault();
																return;
															}
															// `setData` is required by Firefox + some chromium
															// configurations — without it `dragstart` is aborted
															// silently and `dragover`/`drop` never fire.
															e.dataTransfer.setData('text/plain', `${rowIndex}:${seatIndex}`);
															e.dataTransfer.effectAllowed = 'move';
															handleDragStart(rowIndex, seatIndex);
														}}
														onDragOver={e => {
															// preventDefault on the element AND set dropEffect so
															// the browser actually allows the drop on this target.
															e.preventDefault();
															e.dataTransfer.dropEffect = 'move';
														}}
														onDrop={e => {
															e.preventDefault();
															e.stopPropagation();
															handleDrop(rowIndex, seatIndex);
														}}
													>
														{pax ? (
															<div className="w-full min-w-0 pr-3.5">
																{rowIndex === 0 && seatIndex === 0 && (
																	<p className="text-[9px] uppercase tracking-wider font-bold text-primary-600 leading-none mb-0.5">
																		Pilot
																	</p>
																)}
																<p
																	className="text-xs font-semibold text-default-800 truncate leading-tight"
																	title={pax.fullName || 'Passenger'}
																>
																	{seatDisplayName(pax.fullName) || 'Passenger'}
																</p>
																<p className="text-[10px] text-default-500 leading-tight mt-0.5">
																	{pax.weight} {pax.unit || unit}
																</p>
																{/* Remove button — absolute top-right; does not steal name width */}
																{canWriteWb && (
																	<button
																		type="button"
																		aria-label={`Remove ${pax.fullName || 'passenger'}`}
																		className="absolute top-0.5 right-0.5 text-danger opacity-50 hover:opacity-100 transition-opacity text-base leading-none w-4 h-4 flex items-center justify-center"
																		onClick={e => {
																			e.stopPropagation();
																			if (
																				window.confirm(`Remove ${pax.fullName || 'passenger'}?`)
																			) {
																				handlePaxRemove(rowIndex, seatIndex);
																			}
																		}}
																	>
																		×
																	</button>
																)}
															</div>
														) : (
															<div className="w-full flex flex-col items-center justify-center gap-0.5 text-default-400 group-hover:text-primary-500 transition-colors py-0.5">
																<UserPlus className="w-4 h-4" aria-hidden="true" />
																<span className="text-[10px] leading-none font-medium uppercase tracking-wider">
																	Add
																</span>
															</div>
														)}
													</div>
												</div>
											);
										})}
									</div>

									{/* Row-level passenger edit form — full sidebar width when
									    a seat in THIS row is being edited. Replaces the prior
									    per-seat inline form which was too cramped in narrow
									    seat columns. */}
									<AnimatePresence>
										{editingPax?.rowIndex === rowIndex && (
											<motion.div
												key={`pax-form-${rowIndex}`}
												initial={{ opacity: 0, height: 0 }}
												animate={{ opacity: 1, height: 'auto' }}
												exit={{ opacity: 0, height: 0 }}
												transition={{ duration: 0.18 }}
												className="overflow-hidden"
											>
												<div className="mt-2 rounded-lg border border-primary-200 bg-primary-50/30 p-3 space-y-2">
													<p className="text-xs font-semibold text-default-500">
														Seat {editingPax.seatIndex + 1}
													</p>
													<Input
														size="sm"
														variant="bordered"
														label="Full name"
														aria-label="Passenger full name"
														value={paxForm.fullName}
														onValueChange={v => setPaxForm(f => ({ ...f, fullName: v }))}
														isDisabled={isBusy}
														autoFocus
														classNames={{ inputWrapper: 'border-1' }}
													/>
													<div className="flex gap-1.5">
														{/* Rem38 M5 — sane UX guard (variant B): cap at 10
														    chars (`9999999.99`, matching legacy
														    `DECIMAL_NON_ZERO_PATTERN` upper bound). Backend
														    has no limit; legacy day-view has none either —
														    this is a stricter type-time UX guard, not a
														    regression. `maxLength` is ignored by browsers on
														    `type="number"`, so the length is also enforced in
														    the change handler. */}
														<Input
															size="sm"
															variant="bordered"
															label="Weight"
															aria-label="Passenger weight"
															type="number"
															min="0"
															maxLength={10}
															value={paxForm.weight}
															onValueChange={v => {
																if (v.length > 10) return;
																setPaxForm(f => ({ ...f, weight: v }));
															}}
															isDisabled={isBusy}
															classNames={{ base: 'flex-1', inputWrapper: 'border-1' }}
														/>
														<Select
															size="sm"
															variant="bordered"
															aria-label="Weight unit"
															selectedKeys={[paxForm.unit]}
															onSelectionChange={keys => {
																const sel = [...keys][0];
																if (sel) setPaxForm(f => ({ ...f, unit: sel }));
															}}
															isDisabled={isBusy}
															classNames={{ base: 'w-24', trigger: 'h-full border-1' }}
														>
															{Object.values(WEIGHT_UNITS)
																.filter(u => u !== '%' && u !== 'gal' && u !== 'liters')
																.map(u => (
																	<SelectItem key={u}>{u}</SelectItem>
																))}
														</Select>
													</div>
													{/* Live weight conversion hint — legacy parity
													    (`passenger.modal.js:217-223`). Renders the opposite
													    unit's value as a small caption beneath the weight row. */}
													{(() => {
														const n = Number(paxForm.weight);
														if (!Number.isFinite(n) || n <= 0) return null;
														if (paxForm.unit === 'lbs') {
															return (
																<p className="text-[11px] text-default-500 -mt-1">
																	≈ {convertLbsToKg(n)} kg
																</p>
															);
														}
														if (paxForm.unit === 'kg') {
															return (
																<p className="text-[11px] text-default-500 -mt-1">
																	≈ {convertKgToLbs(n)} lbs
																</p>
															);
														}
														return null;
													})()}
													<Input
														size="sm"
														variant="bordered"
														label="Phone (optional)"
														aria-label="Passenger phone number"
														type="tel"
														inputMode="tel"
														maxLength={20}
														value={paxForm.phoneNumber}
														onValueChange={v =>
															setPaxForm(f => ({
																...f,
																phoneNumber: v
																	.split('')
																	.filter(ch => '0123456789 +()-'.includes(ch))
																	.join('')
																	.slice(0, 20)
															}))
														}
														isDisabled={isBusy}
														classNames={{ inputWrapper: 'border-1' }}
													/>
													<Input
														size="sm"
														variant="bordered"
														label="Email (optional)"
														aria-label="Passenger email"
														type="email"
														value={paxForm.email}
														onValueChange={v => setPaxForm(f => ({ ...f, email: v }))}
														isDisabled={isBusy}
														classNames={{ inputWrapper: 'border-1' }}
													/>
													{/* W2 — Choose from event participants (legacy parity). Source =
													    `draft.availablePassengers`; Apply fills the form without
													    submitting; hidden when none apply / all already seated. */}
													{availableParticipants.length > 0 && (
														<div className="flex flex-col gap-1 pt-1 border-t border-default-200">
															<p className="text-[11px] font-medium text-default-500">
																Choose from event participants:
															</p>
															{availableParticipants.map(p => (
																<div
																	key={String(p.userPk)}
																	className="flex items-center justify-between gap-2"
																>
																	<span className="text-xs text-default-700 truncate">
																		{PARTICIPANT_ROLE_LABELS[p.type] || 'Participant'} -{' '}
																		{p.fullName}
																	</span>
																	<Button
																		size="sm"
																		variant="flat"
																		color="primary"
																		className="h-6 min-w-0 px-2 shrink-0"
																		isDisabled={isBusy}
																		onPress={() => handleApplyParticipant(p)}
																	>
																		Apply
																	</Button>
																</div>
															))}
														</div>
													)}
													<div className="flex gap-1.5 pt-0.5">
														<Button
															size="sm"
															color="primary"
															fullWidth
															isLoading={recalculating}
															isDisabled={
																isBusy ||
																!paxForm.fullName.trim() ||
																!paxForm.weight ||
																Number(paxForm.weight) <= 0
															}
															onPress={handlePaxSave}
														>
															Save
														</Button>
														<Button
															size="sm"
															variant="light"
															fullWidth
															isDisabled={isBusy}
															onPress={handlePaxClose}
														>
															Cancel
														</Button>
													</div>
												</div>
											</motion.div>
										)}
									</AnimatePresence>
								</div>
							))}
						</div>

						{/* ── Middle: W&B fields table ── */}
						{/* DIV+ROLE pattern (legacy parity: MetersSection in ResourcePanel.js)
						    avoids the global `:not([role])` strip-rule that nullifies
						    padding on `<td>` / `<th>`. CSS Grid handles column widths. */}
						{/* Vertical scroll + gutter are DESKTOP-3-col only. NO horizontal
						    scroll — the table columns go fluid on narrow so everything fits
						    the width like legacy (epic REM-WBTableFit, supersedes W3). */}
						<div className="xl:overflow-y-auto xl:pl-2">
							<div role="table" aria-label="Weight & Balance fields" className="w-full text-xs">
								<div
									role="row"
									className="grid grid-cols-[minmax(0,1fr)_3.5rem_4rem_4rem] md:grid-cols-[minmax(0,1fr)_5rem_4rem_5rem] xl:grid-cols-[210px_85px_90px_100px] gap-2 px-2 py-1.5 border-b border-default-200 text-xs font-semibold uppercase tracking-wide text-default-500"
								>
									<div role="columnheader" className="text-right">
										Field
									</div>
									<div role="columnheader" className="text-right">
										Weight
									</div>
									<div role="columnheader" className="text-right">
										Arm
									</div>
									<div role="columnheader" className="text-right">
										Moment
									</div>
								</div>
								{tableFields.map((field, idx) => {
									const isStrong = STRONG_PARAMETERS.includes(field.title);
									// Month V2 enhancement over legacy — colored row background
									// for the three strong rows reinforces the ✓ / ✕ status icon:
									//   status: []          → within limits → green tint + accent
									//   status: [...errors] → out of limits → red tint + accent
									// Adapter (`buildWeightBalanceRows`) writes `status` on the
									// field directly, NOT inside `field.weight`.
									const strongStatus = isStrong ? field.status : null;
									const isStrongOk =
										isStrong && Array.isArray(strongStatus) && strongStatus.length === 0;
									const isStrongBad =
										isStrong && Array.isArray(strongStatus) && strongStatus.length > 0;
									const rowClass = isStrongOk
										? 'border-b border-default-100'
										: isStrongBad
											? 'border-b border-default-100'
											: 'border-b border-default-100 hover:bg-default-50';
									// Use inline hsl() with alpha so the tint is guaranteed to
									// render — `bg-success-50` / `bg-danger-50` may be purged from
									// the production Tailwind bundle.
									const strongRowStyle = isStrongOk
										? {
												backgroundColor: 'hsl(var(--heroui-success) / 0.12)',
												boxShadow: 'inset 3px 0 0 hsl(var(--heroui-success))'
											}
										: isStrongBad
											? {
													backgroundColor: 'hsl(var(--heroui-danger) / 0.12)',
													boxShadow: 'inset 3px 0 0 hsl(var(--heroui-danger))'
												}
											: undefined;

									const isEditable = field.weight?.editable;
									const isSelect = field.weight?.select;
									const value = field.weight?.value ?? '';
									const fieldIndex = fields.indexOf(field);

									return (
										<div
											key={`${field.id}-${idx}`}
											role="row"
											className={`grid grid-cols-[minmax(0,1fr)_3.5rem_4rem_4rem] md:grid-cols-[minmax(0,1fr)_5rem_4rem_5rem] xl:grid-cols-[210px_85px_90px_100px] gap-2 px-2 py-1 items-center ${rowClass}`}
											style={strongRowStyle || undefined}
										>
											<div
												role="cell"
												className={`text-right leading-tight ${
													isStrong
														? 'font-semibold text-default-800'
														: 'font-medium text-default-700'
												}`}
											>
												{field.title}
											</div>
											<div role="cell" className="text-right">
												{isEditable ? (
													<div className="flex justify-end">
														<Input
															size="sm"
															variant="bordered"
															classNames={{
																base: 'w-full max-w-[80px]',
																input: 'text-right text-xs',
																inputWrapper: 'h-7 min-h-7 px-1.5 border-1'
															}}
															value={value}
															onValueChange={val => handleChange(fieldIndex, val)}
														/>
													</div>
												) : (
													<span className={isStrong ? 'font-bold text-default-900' : ''}>
														{formatNum(value)}
													</span>
												)}
											</div>
											<div role="cell" className="text-right text-default-600">
												{isSelect ? (
													<div className="flex justify-end">
														<Select
															size="sm"
															variant="bordered"
															aria-label="Fuel load unit"
															selectedKeys={[unit]}
															onSelectionChange={keys => {
																const sel = [...keys][0];
																if (sel) handleChangeFuelLoadType(sel);
															}}
															classNames={{
																base: 'w-full min-w-[64px]',
																trigger: 'h-7 min-h-7 border-1',
																// Listbox popover isn't clamped to the narrow trigger —
																// fits the longest label ("liters") on tablet/phone
																// (epic REM-FuelUnitMenuWidth).
																popoverContent: 'min-w-[7rem]'
															}}
														>
															{Object.values(WEIGHT_UNITS).map(u => (
																<SelectItem key={u} className="whitespace-nowrap">
																	{u}
																</SelectItem>
															))}
														</Select>
													</div>
												) : (
													formatNum(field.arm)
												)}
											</div>
											<div role="cell" className="text-right text-default-600 whitespace-nowrap">
												{!isSelect && formatNum(field.moment)}
											</div>
										</div>
									);
								})}
							</div>

							{/* Status messages */}
							{statusMessages.length > 0 && (
								<ul className="mt-3 space-y-1">
									{statusMessages.map((item, i) => (
										<li
											key={i}
											className={`text-xs flex items-center gap-1.5 ${item.success ? 'text-success' : 'text-danger'}`}
										>
											<span>{item.success ? '✓' : '✕'}</span>
											<span>{item.message}</span>
										</li>
									))}
								</ul>
							)}

							{/* Recalculate — full-width primary button under the table (legacy
							    parity: `weight-and-balance.js` renders Recalculate full-width
							    immediately under the W&B table). */}
							<div className="mt-4">
								<Button
									color="primary"
									variant="solid"
									fullWidth
									isLoading={recalculating}
									isDisabled={recalculating || saving}
									onPress={handleRecalculate}
									startContent={!recalculating && <RefreshCcw className="w-4 h-4" />}
									className="h-11 font-semibold"
								>
									Recalculate
								</Button>
							</div>

							{/* Global warning entries from the response (mirrors legacy
							    status pills under the table) — only ACTUAL errors, no
							    per-row "within limits" duplicates (row colors already
							    communicate that). */}
							{(() => {
								const warningItems = [];
								fields.forEach(f => {
									if (!Array.isArray(f.status)) return;
									f.status.forEach(it => {
										if (it && it.message && !it.success) {
											warningItems.push(it.message);
										}
									});
								});
								if (warningItems.length === 0) return null;
								return (
									<ul className="mt-3 flex flex-col gap-1 text-xs">
										{warningItems.map((msg, i) => (
											<li key={i} className="flex items-center gap-1.5 text-warning-700">
												<AlertTriangle className="w-3.5 h-3.5 text-warning shrink-0" />
												<span>{msg}</span>
											</li>
										))}
									</ul>
								);
							})()}
						</div>

						{/* ── Right: Fuel mini-table + Envelope chart ── */}
						{/* md-xl (2-col): drop to a full-width row BELOW passengers + table.
						    xl (3-col): 3rd column. (epic W1, breakpoint raised by REM-WB1024) */}
						{/* In the 2-col range (md–lg) this panel spans the full row, so the
						    fuel table + CG Envelope sit SIDE BY SIDE (each ~half). Phone (<md)
						    and the xl 3-col (narrow column) stack them vertically (epic
						    REM-WB2colBalance). */}
						<div className="grid grid-cols-1 md:grid-cols-2 xl:flex xl:flex-col gap-4 min-w-0 md:col-span-full xl:col-span-1 xl:overflow-y-auto xl:pl-2 xl:border-l xl:border-default-100">
							{draft.fuelWeightTable && fuelTableKeys.length > 0 && (
								<div className="min-w-0 xl:flex-none">
									{/* DIV+ROLE pattern — same reason as the W&B fields table above. */}
									<div role="table" aria-label="Fuel cases" className="w-full text-xs">
										<div
											role="row"
											className="grid grid-cols-[minmax(0,1fr)_44px_38px_50px_44px] gap-1 px-2 py-1.5 border-b border-default-200 text-default-500 font-medium"
										>
											<div role="columnheader"></div>
											<div role="columnheader" className="text-right">
												Time
											</div>
											<div role="columnheader" className="text-right">
												%
											</div>
											<div role="columnheader" className="text-right">
												Lbs
											</div>
											<div role="columnheader" className="text-right">
												Gal
											</div>
										</div>
										{fuelTableKeys.map(key => {
											const row = draft.fuelWeightTable[key];
											const { title = key, time = '0', percent = '0', lbs = '0', gal = '0' } = row;
											const canCopy = copyKeys.includes(key);
											return (
												<div
													key={key}
													role="row"
													className="grid grid-cols-[minmax(0,1fr)_44px_38px_50px_44px] gap-1 px-2 py-1.5 items-center border-b border-default-100"
												>
													<div
														role="cell"
														className="text-default-600 inline-flex items-center gap-1 min-w-0"
													>
														{/* Reserve a fixed slot for the Copy button on EVERY row
														    so titles align in the same column regardless of
														    whether the row supports copy. */}
														<span className="w-5 h-5 inline-flex items-center justify-center shrink-0">
															{canCopy && (
																<Button
																	isIconOnly
																	size="sm"
																	variant="light"
																	aria-label={`Copy ${title} to Fuel Load`}
																	title="Copy to Fuel Load"
																	className="w-5 h-5 min-w-5 text-primary"
																	onPress={() => handleFuelCopy(key)}
																>
																	<Copy className="w-3 h-3" />
																</Button>
															)}
														</span>
														<span className="truncate">{title}</span>
													</div>
													<div role="cell" className="text-right text-default-600 tabular-nums">
														{parseFloat(time).toFixed(2)}
													</div>
													<div role="cell" className="text-right text-default-600 tabular-nums">
														{parseFloat(percent).toFixed(2)}
													</div>
													<div role="cell" className="text-right text-default-600 tabular-nums">
														{parseFloat(lbs).toFixed(2)}
													</div>
													<div role="cell" className="text-right text-default-600 tabular-nums">
														{parseFloat(gal).toFixed(2)}
													</div>
												</div>
											);
										})}
									</div>
								</div>
							)}

							{hasEnvelopeData && (
								<div className="min-w-0 xl:flex-1 xl:min-h-0 xl:flex xl:flex-col">
									<p className="text-xs font-semibold uppercase tracking-wide text-default-400 mb-2">
										CG Envelope
									</p>
									{/* Cap + center the chart so it doesn't balloon to full width
									    when the panel spans the md row / a wide desktop column
									    (epic W2); it still scales DOWN with the container on narrow.
									    The square side ALSO caps by viewport height (owner: the
									    whole W&B screen must fit with no inner scrolling) — short
									    laptops shrink the chart instead of scrolling the column. */}
									<div className="w-full mx-auto max-w-[480px] h-[280px] xl:h-auto xl:flex-1 xl:min-h-[180px] xl:max-h-[400px]">
										<EnvelopeChart
											ref={envelopeRef}
											forwardLimits={forwardLimits}
											aftLimits={aftLimits}
											wbPoints={wbPoints}
											fuelCasePoints={fuelCasePoints}
											unitLabels={unitLabels}
											height="100%"
										/>
									</div>
								</div>
							)}

							{!hasEnvelopeData && (
								<div className="rounded-lg border border-default-200 bg-default-50 p-4 text-center">
									<p className="text-xs text-default-400">No envelope data</p>
								</div>
							)}
						</div>
					</div>
				)}
			</ModalBody>

			<ModalFooter className="gap-2 justify-end border-t border-default-200 pt-3">
				<Button size="sm" variant="light" onPress={() => onBack?.()}>
					Close
				</Button>
				<Tooltip
					content={!wbWithinLimits ? 'Available only when W&B is within limits' : 'Print manifest'}
					placement="top"
					isDisabled={false}
				>
					{/* Wrap in span so Tooltip can render over a disabled button (a
					    `disabled` button doesn't fire pointer events natively). */}
					<span>
						<Button
							size="sm"
							variant="flat"
							isLoading={printing}
							isDisabled={
								loading ||
								!!error ||
								!wbPayload ||
								printing ||
								saving ||
								recalculating ||
								!wbWithinLimits
							}
							onPress={handlePrintManifest}
						>
							Print Manifest
						</Button>
					</span>
				</Tooltip>
				<Button
					size="sm"
					color="primary"
					isLoading={saving}
					isDisabled={loading || saving || recalculating || !canWriteWb}
					onPress={handleSave}
				>
					Save
				</Button>
			</ModalFooter>
		</>
	);
}

export default WeightBalanceScreen;
